import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, Link } from "react-router-dom";
import { TfiAngleLeft } from "react-icons/tfi";
import { FiPlus, FiTrash2, FiArrowUp, FiArrowDown } from "react-icons/fi";
import axios from "axios";
import { useNavContext } from "../context/nav_context";
import { Loading } from "../components";
import {
  api_v3_get_tournament_details_admin,
  api_v3_update_tournament_details,
} from "../utils/constants";

const AdminTournamentDetails = () => {
  const { tournamentId } = useParams();
  const { user } = useNavContext();
  const [isLoading, setIsLoading] = useState(true);
  const [tournamentDetails, setTournamentDetails] = useState(null);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const [image, setImage] = useState("");
  const [handbookLink, setHandbookLink] = useState("");
  const [maps, setMaps] = useState([]);
  const [drafts, setDrafts] = useState({ draft_PA3: { mapLink: "", civLink: "" }, draft_BO5: { mapLink: "", civLink: "" } });
  const [descriptions, setDescriptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(api_v3_get_tournament_details_admin(tournamentId));
        setTournamentDetails(response.data);
        setImage(response.data.image || "");
        setHandbookLink(response.data.handbookLink || "");
        setMaps(response.data.maps || []);
        setDrafts(response.data.drafts || { draft_PA3: { mapLink: "", civLink: "" }, draft_BO5: { mapLink: "", civLink: "" } });
        setDescriptions(response.data.tournamentDescriptions || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching tournament details:", err);
        setError("Error al cargar los detalles del torneo");
        setImage(""); setHandbookLink(""); setMaps([]);
        setDrafts({ draft_PA3: { mapLink: "", civLink: "" }, draft_BO5: { mapLink: "", civLink: "" } });
        setDescriptions([]);
      } finally { setIsLoading(false); }
    };
    fetchData();
  }, [tournamentId]);

  const formatDateForInput = (utcDateString) => {
    if (!utcDateString) return "";
    const date = new Date(utcDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const convertToUTC = (inputDateString) => {
    if (!inputDateString) return "";
    return new Date(inputDateString).toISOString();
  };

  const prepareDataForSave = () => ({
    image, handbookLink,
    maps: maps.map((map) => (map.revealDate && !map.revealDate.endsWith("Z") ? { ...map, revealDate: convertToUTC(map.revealDate) } : map)),
    drafts, tournamentDescriptions: descriptions, tournamentId,
  });

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const method = tournamentDetails ? "put" : "post";
      await axios({ method, url: api_v3_update_tournament_details(tournamentId), data: prepareDataForSave() });
      setSaveMessage({ type: "success", text: "Detalles guardados correctamente" });
      const response = await axios.get(api_v3_get_tournament_details_admin(tournamentId));
      setTournamentDetails(response.data);
    } catch (err) {
      console.error("Error saving tournament details:", err);
      setSaveMessage({ type: "error", text: "Error al guardar los detalles del torneo" });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const addMap = () => setMaps([...maps, { id: maps.length > 0 ? Math.max(...maps.map((m) => m.id)) + 1 : 1, mapName: "", mapImage: "", revealDate: new Date().toISOString() }]);
  const updateMap = (i, f, v) => setMaps(maps.map((m, idx) => idx === i ? { ...m, [f]: v } : m));
  const removeMap = (i) => setMaps(maps.filter((_, idx) => idx !== i));
  const moveMapUp = (i) => { if (i === 0) return; const arr = [...maps]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setMaps(arr); };
  const moveMapDown = (i) => { if (i === maps.length - 1) return; const arr = [...maps]; [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; setMaps(arr); };
  const addDescription = () => setDescriptions([...descriptions, { id: descriptions.length > 0 ? Math.max(...descriptions.map((d) => d.id)) + 1 : 1, title: "", text: "" }]);
  const updateDescription = (i, f, v) => setDescriptions(descriptions.map((d, idx) => idx === i ? { ...d, [f]: v } : d));
  const removeDescription = (i) => setDescriptions(descriptions.filter((_, idx) => idx !== i));
  const updateDraft = (type, field, value) => setDrafts({ ...drafts, [type]: { ...drafts[type], [field]: value } });

  if (!user) return <Canvas><EmptyState>Debes iniciar sesión para acceder a este perfil.</EmptyState></Canvas>;
  if (!user.isAdmin) return <Canvas><EmptyState>Lo siento majete, pero estás intentando acceder a donde no debes.</EmptyState></Canvas>;
  if (isLoading) return <Canvas><Loading /></Canvas>;

  return (
    <Canvas>
      <Content>
        <HeaderRow>
          <PageTitle>DETALLES DEL TORNEO</PageTitle>
          <BackLink to="/admin/"><TfiAngleLeft /> ADMIN HOME</BackLink>
        </HeaderRow>

        {error && <AlertError>{error}</AlertError>}
        {saveMessage && <AlertSave $type={saveMessage.type}>{saveMessage.text}</AlertSave>}

        {/* General Info */}
        <SectionCard>
          <SectionTitle>INFORMACIÓN GENERAL</SectionTitle>
          <Field>
            <FieldLabel>URL de la imagen del torneo</FieldLabel>
            <TextInput value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://ejemplo.com/imagen.png" />
          </Field>
          <Field>
            <FieldLabel>URL del manual del torneo</FieldLabel>
            <TextInput value={handbookLink} onChange={(e) => setHandbookLink(e.target.value)} placeholder="https://docs.google.com/document/..." />
          </Field>
          {image && (
            <PreviewBlock>
              <PreviewTitle>VISTA PREVIA</PreviewTitle>
              <PreviewImg src={image} alt="" />
            </PreviewBlock>
          )}
        </SectionCard>

        {/* Drafts */}
        <SectionCard>
          <SectionTitle>CONFIGURACIÓN DE DRAFTS</SectionTitle>
          <SubGrid>
            <SubCard>
              <SubTitle>DRAFT PA3</SubTitle>
              <Field>
                <FieldLabel>URL draft de mapas</FieldLabel>
                <TextInput value={drafts.draft_PA3?.mapLink || ""} onChange={(e) => updateDraft("draft_PA3", "mapLink", e.target.value)} placeholder="https://aoe2cm.net/preset/..." />
              </Field>
              <Field>
                <FieldLabel>URL draft de civilizaciones</FieldLabel>
                <TextInput value={drafts.draft_PA3?.civLink || ""} onChange={(e) => updateDraft("draft_PA3", "civLink", e.target.value)} placeholder="https://aoe2cm.net/preset/..." />
              </Field>
            </SubCard>
            <SubCard>
              <SubTitle>DRAFT BO5</SubTitle>
              <Field>
                <FieldLabel>URL draft de mapas</FieldLabel>
                <TextInput value={drafts.draft_BO5?.mapLink || ""} onChange={(e) => updateDraft("draft_BO5", "mapLink", e.target.value)} placeholder="https://aoe2cm.net/preset/..." />
              </Field>
              <Field>
                <FieldLabel>URL draft de civilizaciones</FieldLabel>
                <TextInput value={drafts.draft_BO5?.civLink || ""} onChange={(e) => updateDraft("draft_BO5", "civLink", e.target.value)} placeholder="https://aoe2cm.net/preset/..." />
              </Field>
            </SubCard>
          </SubGrid>
        </SectionCard>

        {/* Maps */}
        <SectionCard>
          <SectionHeaderRow>
            <SectionTitle>MAPAS</SectionTitle>
            <AddBtn onClick={addMap}><FiPlus /> AÑADIR MAPA</AddBtn>
          </SectionHeaderRow>
          {maps.length === 0 ? (
            <MutedText>No hay mapas configurados. Añade uno con el botón de arriba.</MutedText>
          ) : (
            maps.map((map, i) => (
              <MapBlock key={i}>
                <MapHeaderRow>
                  <MapNumber>MAPA #{i + 1}</MapNumber>
                  <MapActions>
                    {i > 0 && <IconBtn onClick={() => moveMapUp(i)} title="Mover arriba"><FiArrowUp /></IconBtn>}
                    {i < maps.length - 1 && <IconBtn onClick={() => moveMapDown(i)} title="Mover abajo"><FiArrowDown /></IconBtn>}
                    <IconBtn $danger onClick={() => removeMap(i)}><FiTrash2 /></IconBtn>
                  </MapActions>
                </MapHeaderRow>
                <Field>
                  <FieldLabel>ID</FieldLabel>
                  <TextInput type="number" value={map.id} onChange={(e) => updateMap(i, "id", parseInt(e.target.value))} />
                </Field>
                <Field>
                  <FieldLabel>Nombre del mapa</FieldLabel>
                  <TextInput value={map.mapName} onChange={(e) => updateMap(i, "mapName", e.target.value)} placeholder="Nombre del mapa" />
                </Field>
                <Field>
                  <FieldLabel>URL de la imagen</FieldLabel>
                  <TextInput value={map.mapImage} onChange={(e) => updateMap(i, "mapImage", e.target.value)} placeholder="https://ejemplo.com/mapa.png" />
                </Field>
                <Field>
                  <FieldLabel>Fecha de publicación (hora española)</FieldLabel>
                  <TextInput type="datetime-local" value={map.revealDate ? formatDateForInput(map.revealDate) : ""} onChange={(e) => updateMap(i, "revealDate", e.target.value)} />
                </Field>
                {map.mapImage && <PreviewImg src={map.mapImage} alt={map.mapName} style={{ maxHeight: "120px" }} />}
              </MapBlock>
            ))
          )}
        </SectionCard>

        {/* Descriptions */}
        <SectionCard>
          <SectionHeaderRow>
            <SectionTitle>DESCRIPCIONES</SectionTitle>
            <AddBtn onClick={addDescription}><FiPlus /> AÑADIR DESCRIPCIÓN</AddBtn>
          </SectionHeaderRow>
          {descriptions.length === 0 ? (
            <MutedText>No hay descripciones configuradas.</MutedText>
          ) : (
            descriptions.map((desc, i) => (
              <DescBlock key={i}>
                <DescHeaderRow>
                  <MapNumber>DESCRIPCIÓN #{i + 1}</MapNumber>
                  <IconBtn $danger onClick={() => removeDescription(i)}><FiTrash2 /></IconBtn>
                </DescHeaderRow>
                <Field>
                  <FieldLabel>ID</FieldLabel>
                  <TextInput type="number" value={desc.id} onChange={(e) => updateDescription(i, "id", parseInt(e.target.value))} />
                </Field>
                <Field>
                  <FieldLabel>Título</FieldLabel>
                  <TextInput value={desc.title} onChange={(e) => updateDescription(i, "title", e.target.value)} placeholder="Título" />
                </Field>
                <Field>
                  <FieldLabel>Texto (HTML permitido)</FieldLabel>
                  <TextAreaInput value={desc.text} onChange={(e) => updateDescription(i, "text", e.target.value)} placeholder="<div><p>Contenido HTML...</p></div>" rows={8} />
                </Field>
                {desc.text && (
                  <PreviewBlock>
                    <PreviewTitle>VISTA PREVIA</PreviewTitle>
                    <div dangerouslySetInnerHTML={{ __html: desc.text }} />
                  </PreviewBlock>
                )}
              </DescBlock>
            ))
          )}
        </SectionCard>

        <SaveRow>
          <SaveBtn onClick={handleSave} disabled={isSaving}>
            {isSaving ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
          </SaveBtn>
        </SaveRow>
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`background: var(--v-canvas); min-height: 100vh; display: flex; justify-content: center; padding: 4rem 1.5rem;`;
const Content = styled.div`width: 100%; max-width: 900px;`;
const PageTitle = styled.h1`font-family: Anton, sans-serif; font-size: clamp(36px, 6vw, 56px); font-weight: 400; text-transform: uppercase; line-height: 1; color: var(--v-fg); margin: 0;`;
const EmptyState = styled.p`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg-muted); text-align: center; padding: 4rem 1rem;`;
const HeaderRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 2.5rem; flex-wrap: wrap; gap: 1rem;`;
const BackLink = styled(Link)`display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-mint); text-decoration: none; padding: 0.5rem 0.9rem; border: 1px solid rgba(60,255,208,0.2); border-radius: 24px; transition: background 0.15s ease; &:hover { background: rgba(60,255,208,0.04); }`;
const AlertError = styled.div`background: rgba(255,59,59,0.1); border: 1px solid rgba(255,59,59,0.2); color: #ff3b3b; font-family: var(--v-font-mono); font-size: 11px; padding: 0.6rem 0.85rem; border-radius: 10px; margin-bottom: 1rem;`;
const AlertSave = styled.div`background: ${({ $type }) => $type === "success" ? "rgba(60,255,208,0.08)" : "rgba(255,59,59,0.1)"}; border: 1px solid ${({ $type }) => $type === "success" ? "rgba(60,255,208,0.15)" : "rgba(255,59,59,0.2)"}; color: ${({ $type }) => $type === "success" ? "var(--v-mint)" : "#ff3b3b"}; font-family: var(--v-font-mono); font-size: 11px; padding: 0.6rem 0.85rem; border-radius: 10px; margin-bottom: 1rem;`;
const SectionCard = styled.div`background: var(--v-surface); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; padding: 1.5rem; margin-bottom: 1.25rem;`;
const SectionTitle = styled.h3`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--v-fg-muted); margin: 0 0 1rem;`;
const SectionHeaderRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.5rem;`;
const Field = styled.div`margin-bottom: 0.85rem;`;
const FieldLabel = styled.label`display: block; font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); margin-bottom: 0.3rem;`;
const TextInput = styled.input`width: 100%; padding: 0.6rem 0.85rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; color: var(--v-fg); font-family: var(--v-font-body); font-size: 13px; outline: none; transition: border-color 0.15s ease; box-sizing: border-box; &:focus { border-color: var(--v-mint); } &::placeholder { color: var(--v-fg-muted); opacity: 0.4; }`;
const TextAreaInput = styled.textarea`width: 100%; padding: 0.6rem 0.85rem; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; color: var(--v-fg); font-family: var(--v-font-body); font-size: 13px; outline: none; resize: vertical; box-sizing: border-box; &:focus { border-color: var(--v-mint); } &::placeholder { color: var(--v-fg-muted); opacity: 0.4; }`;
const AddBtn = styled.button`display: inline-flex; align-items: center; gap: 0.4rem; background: rgba(60,255,208,0.08); border: 1px solid rgba(60,255,208,0.2); color: var(--v-mint); font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.4rem 0.8rem; border-radius: 20px; cursor: pointer; transition: background 0.15s ease; &:hover { background: rgba(60,255,208,0.14); }`;
const MutedText = styled.p`font-family: var(--v-font-body); font-size: 13px; color: var(--v-fg-muted); margin: 0;`;
const PreviewBlock = styled.div`margin-top: 0.75rem; padding: 0.75rem; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 12px;`;
const PreviewTitle = styled.p`font-family: var(--v-font-mono); font-size: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); margin: 0 0 0.5rem;`;
const PreviewImg = styled.img`max-width: 100%; max-height: 160px; border-radius: 8px; object-fit: contain;`;
const SubGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; @media (max-width: 640px) { grid-template-columns: 1fr; }`;
const SubCard = styled.div`background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 16px; padding: 1rem;`;
const SubTitle = styled.h4`font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); margin: 0 0 0.75rem;`;
const MapBlock = styled.div`background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 16px; padding: 1rem; margin-bottom: 0.75rem;`;
const MapHeaderRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;`;
const MapNumber = styled.span`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; color: var(--v-fg);`;
const MapActions = styled.div`display: flex; gap: 4px;`;
const IconBtn = styled.button`width: 1.8rem; height: 1.8rem; display: flex; align-items: center; justify-content: center; border-radius: 50%; border: 1px solid ${({ $danger }) => $danger ? "rgba(255,59,59,0.2)" : "rgba(255,255,255,0.1)"}; background: transparent; color: ${({ $danger }) => $danger ? "#ff3b3b" : "var(--v-fg-muted)"}; font-size: 12px; cursor: pointer; transition: border-color 0.15s ease; &:hover { border-color: ${({ $danger }) => $danger ? "#ff3b3b" : "var(--v-mint)"}; }`;
const DescBlock = styled.div`background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 16px; padding: 1rem; margin-bottom: 0.75rem;`;
const DescHeaderRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;`;
const SaveRow = styled.div`display: flex; justify-content: flex-end; margin-top: 1rem;`;
const SaveBtn = styled.button`background: var(--v-mint); border: none; color: #000; font-family: var(--v-font-mono); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.65rem 2rem; border-radius: 24px; cursor: pointer; transition: opacity 0.15s ease; &:hover:not(:disabled) { opacity: 0.85; } &:disabled { opacity: 0.4; cursor: not-allowed; }`;

export default AdminTournamentDetails;
